import BigNumber from 'bignumber.js';
import { currencyCodeSchema } from './currencies.js';
import { CurrencyBN } from './currencyBN.js';
import { z } from 'zod';
/** Zod schema for a {@link Money}. */
export const moneySchema = z.object({
    amountCents: z.number().int(),
    currencyCode: currencyCodeSchema,
});
const ROUNDING_MODES = {
    'half-even': BigNumber.ROUND_HALF_EVEN,
    'half-up': BigNumber.ROUND_HALF_UP,
    'half-down': BigNumber.ROUND_HALF_DOWN,
    up: BigNumber.ROUND_UP,
    down: BigNumber.ROUND_DOWN,
    ceil: BigNumber.ROUND_CEIL,
    floor: BigNumber.ROUND_FLOOR,
};
const DEFAULT_ROUNDING = 'half-even';
const assertSameCurrency = (a, b) => {
    if (a.currencyCode !== b.currencyCode) {
        throw new Error(`Cannot combine Money of differing currencies: ${a.currencyCode} and ${b.currencyCode}`);
    }
};
/**
 * Construct a {@link Money} from an integer minor-unit amount, validating that
 * the amount is a whole number of minor units.
 */
const create = (amountCents, currencyCode) => {
    if (!Number.isSafeInteger(amountCents)) {
        throw new Error(`Money.amountCents must be a safe integer value; got ${amountCents}`);
    }
    return { amountCents, currencyCode };
};
/** The most weights `allocate` will split across in a single call. */
const MAX_RATIOS = 1000;
/** The largest individual weight `allocate` accepts. */
const MAX_WEIGHT = 1_000_000;
/** Distribute `amount` across integer weight `ratios`, conserving the total. */
const allocate = (money, ratios) => {
    if (ratios.length === 0) {
        throw new Error('Money.allocate requires at least one ratio');
    }
    if (ratios.length > MAX_RATIOS) {
        throw new Error(`Money.allocate accepts at most ${MAX_RATIOS} ratios`);
    }
    if (ratios.some((r) => !Number.isInteger(r) || r < 0 || r > MAX_WEIGHT)) {
        throw new Error(`Money.allocate ratios must be non-negative integers no greater than ${MAX_WEIGHT}`);
    }
    const totalNum = ratios.reduce((sum, r) => sum + r, 0);
    if (totalNum <= 0) {
        throw new Error('Money.allocate ratios must sum to a positive number');
    }
    // Note: we use BigInt for intermediate multiplication steps here, to avoid
    // concerns about precision loss or unsafe integer ranges.
    const amount = BigInt(money.amountCents);
    const total = BigInt(totalNum);
    const parts = ratios.map((ratio, idx) => {
        const weighted = amount * BigInt(ratio);
        let share = weighted / total;
        if (weighted % total !== 0n && weighted < 0n) {
            // BigInt division truncates toward zero, but if `weighted` is negative,
            // then that's not what we want. We want to round toward -Infinity to
            // ensure that our remainder is positive.
            // Shave 1 off of `share` here to correct this.
            share -= 1n;
        }
        const remainder = weighted - share * total;
        return { idx, share, remainder };
    });
    // This distributes most of the value...
    const distributed = parts.reduce((sum, p) => sum + p.share, 0n);
    // ...and the leftover quantity is an integer in [0, ratios.length).
    const leftover = Number(amount - distributed);
    // Sort `parts` by remainder size (descending)...
    const byRemainderDesc = [...parts].sort((a, b) => a.remainder < b.remainder ? 1 : a.remainder > b.remainder ? -1 : 0);
    // ...then traverse this sorted list to drop off the remainder minor units.
    for (let k = 0; k < leftover; k++) {
        parts[byRemainderDesc[k].idx].share += 1n;
    }
    return parts.map((p) => create(Number(p.share), money.currencyCode));
};
/**
 * Operations over {@link Money} values.
 * Includes the following (non-exhaustive):
 *
 * - Arithmetic and unary functions (`add`, `subtract`, `negate`, `isNegative`,
 * `split`, `allocate`...)
 * - A canonical factory function `create(number, CurrencyCode)`, which
 * validates that `amountCents` is specified as a whole number
 * - Additional factory functions for convenience:
 * `zero(CurrencyCode)` and `usdCents(number)`
 *
 * Where applicable, these functions ensure matching currency values and handle
 * under/overflow and rounding (using banker's rounding by default).
 */
export const Money = {
    /** Construct from an integer minor-unit amount. */
    create,
    /** Creates a `Money` instance of the specified amount in USD. */
    usdCents(amountCents) {
        return create(amountCents, 'USD');
    },
    /** Creates a zero-amount `Money` instance in the specified currency. */
    zero(currencyCode) {
        return create(0, currencyCode);
    },
    /** Sum of two same-currency amounts. */
    add(a, b) {
        assertSameCurrency(a, b);
        return create(a.amountCents + b.amountCents, a.currencyCode);
    },
    /** Difference of two same-currency amounts (`a - b`). */
    subtract(a, b) {
        assertSameCurrency(a, b);
        return create(a.amountCents - b.amountCents, a.currencyCode);
    },
    /** This amount negated (e.g. a charge becomes a refund of equal size). */
    negate(money) {
        return create(-money.amountCents, money.currencyCode);
    },
    /**
     * Scale by a factor (a tax rate, a tip percentage, a quantity), rounding the
     * result to a whole minor unit exactly once. Rounds with banker's rounding
     * unless another mode is given.
     *
     * To split an amount among parties, use {@link Money.allocate} /
     * {@link Money.split} instead — repeated `scale` by fractions loses or gains
     * cents.
     */
    scale(money, factor, rounding = DEFAULT_ROUNDING) {
        const scaled = new CurrencyBN(money.amountCents)
            .times(factor)
            .integerValue(ROUNDING_MODES[rounding])
            .toNumber();
        return create(scaled, money.currencyCode);
    },
    /**
     * Distribute an amount across integer weight `ratios`, conserving the total
     * to the cent: the returned parts always sum back to `money`. Leftover minor
     * units from flooring are handed to the parts with the largest fractional
     * remainder (largest-remainder method), so no cent is created or lost.
     *
     * Works for negative amounts (refunds) too. Ratios must be non-negative
     * integers that sum to a positive number — at most 1000 ratios, each no
     * greater than 1,000,000.
     */
    allocate,
    /** Split into `parts` as-even-as-possible amounts, conserving the total. */
    split(money, parts) {
        if (!Number.isInteger(parts) || parts <= 0) {
            throw new Error('Money.split requires a positive integer number of parts');
        }
        return allocate(money, new Array(parts).fill(1));
    },
    /** -1, 0, or 1 — ordering of two same-currency amounts (`a` vs `b`). */
    compare(a, b) {
        assertSameCurrency(a, b);
        if (a.amountCents < b.amountCents)
            return -1;
        if (a.amountCents > b.amountCents)
            return 1;
        return 0;
    },
    /** True when both currency and amount match. Total across currencies. */
    equals(a, b) {
        return a.currencyCode === b.currencyCode && a.amountCents === b.amountCents;
    },
    isZero(money) {
        return money.amountCents === 0;
    },
    isNegative(money) {
        return money.amountCents < 0;
    },
    isPositive(money) {
        return money.amountCents > 0;
    },
};
//# sourceMappingURL=money.js.map